import { ErrorMessage } from "@atlaskit/form";
import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  BankBaseProps,
  BankBaseSchema,
  Bank_InitialState,
  Button,
  ConsentOrdersPathName,
  CustomCheckbox,
  FormNavigation,
  useBank,
  useNames
} from "@fla/components";
import BankIcon from "@fla/components/src/assets/svgs/bank_card.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectAsset, selectBaseAssetsDebts, selectConsentOrdersBase } from "pages/ConsentOrders";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_no_bank: "no"
};

function Bank() {
  const co = useSelector(selectConsentOrdersBase);
  const bankData = useSelector(selectAsset(AssetType.BANK)) as Bank_InitialState[];
  const usenames = useNames({ initialDetails: co.initialDetails });
  const { spouseFirstName } = usenames;
  const assetsDebts = useSelector(selectBaseAssetsDebts);

  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { control, handleSubmit, reset, getValues, watch } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(BankBaseSchema)
  });

  const handleBackClick = () => {
    navigate(ConsentOrdersPathName.Superannuation);
  };

  const editData = (values?: BankBaseProps) => {
    navigate(`${ConsentOrdersPathName.Bank}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const { ownCard, splitCard, userTotalAmount, spouseTotalAmount } = useBank({
    bankData,
    ...usenames
  });

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();

  const onSubmit = async (values: BankBaseProps) => {
    try {
      setStatus(SliceStatus.Pending);

      if (values.if_no_bank === "yes" && bankData.length) {
        setError(
          "If neither of you have any bank accounts in Australia, then please remove the added bank account details."
        );
      } else if (values.if_no_bank !== "yes" && !bankData.length) {
        setError("please add at least one bank account");
      } else {
        setError(undefined);
        const payload = {
          bank: { data: values, isCompleted: true },
          currentStep: 4
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        navigate(ConsentOrdersPathName.Vehicle);
      }
      setStatus(SliceStatus.Resolved);
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else if (assetsDebts?.bank?.data) {
      reset(assetsDebts?.bank?.data);
    } else {
      reset(defaultValues);
    }
  }, [location]);

  return (
    <section className="w-full py-10">
      <form
        id="bank-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5 space-y-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={BankIcon} className="w-10 h-auto mx-auto my-3" alt="superannuation" />

        <h3 className="text-center my-5">Bank Accounts</h3>

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="px-16 text-center text-base font-bold">
            Please add all the bank accounts you and {spouseFirstName} have in Australia
          </p>
          <Button
            className="px-5 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_no_bank") === "yes"}
          >
            + Add Bank Account
          </Button>
        </div>

        <article className="mb-12">
          <CustomCheckbox
            control={control}
            name="if_no_bank"
            title="Neither of us have this"
            valueType="string"
            classes="w-fit mx-auto"
            hideErrorMsg
            onChange={() => setError("")}
          />
          {error ? (
            <div className="max-w-sm mx-auto text-center">
              <ErrorMessage>{error}</ErrorMessage>
            </div>
          ) : null}
        </article>

        {Array.isArray(bankData) && bankData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

            {ownCard("user", editData)}
            {splitCard("user", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>

            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

            {ownCard("spouse", editData)}
            {splitCard("spouse", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          isLast={false}
          form="bank-form"
          handleBackClick={handleBackClick}
          nextButtonText="Continue"
          isSubmitting={status === SliceStatus.Pending}
          classes="mt-12"
        />
      </form>
    </section>
  );
}

export default withConsentOrder(Bank);
